import useCommandFlowStore from "../../store/commandFlowStore";
import {
  storePayload,
  clearAllCommandState,
} from "../../utils/command/commandStateUtils";
import {
  parseMarkedText,
  stripMarkers,
} from "../../utils/ui/outputProcessingUtils";

// Handles a new prompt and calls onStepStart if provided
export async function handleNewPromptAndStep({
  result,
  setCurrentPrompt,
  resolvePrompt,
  onStepStart,
  engine,
}) {
  if (result.prompt && !result.output) {
    const resolvedPrompt = await resolvePrompt(result.prompt);

    // Check if prompt contains multiple lines and split them for delayed display
    if (typeof resolvedPrompt === "string" && resolvedPrompt.includes("\n")) {
      const addOutputWithTypingEffect =
        useCommandFlowStore.getState().addOutputWithTypingEffect;
      const lines = resolvedPrompt.split("\n");
      const lastLine = lines.pop(); // Remove the last line to use as prompt

      if (lines.length > 0) {
        // Join into a single output entry so one Enter skips the whole block.
        // CSS `white-space: pre-wrap` renders the embedded \n as line breaks.
        const outputLines = [
          { type: "output", text: parseMarkedText(lines.join("\n")) },
        ];
        await addOutputWithTypingEffect(outputLines);
        // Re-set isLoading so the ">" doesn't flash before the prompt typing starts
        useCommandFlowStore.setState({ isLoading: true });
      }

      // Set only the last line as the current prompt with typing effect
      const setCurrentPromptWithTypingEffect =
        useCommandFlowStore.getState().setCurrentPromptWithTypingEffect;
      await setCurrentPromptWithTypingEffect(stripMarkers(lastLine));
    } else {
      // Single-line prompt - use typing effect
      const setCurrentPromptWithTypingEffect =
        useCommandFlowStore.getState().setCurrentPromptWithTypingEffect;
      await setCurrentPromptWithTypingEffect(stripMarkers(resolvedPrompt));
    }

    if (onStepStart) {
      onStepStart(engine.currentCommandName);
    }
    return true;
  }
  return false;
}

// Handles step completion with simplified parameters
export async function handleStepComplete({
  result,
  setCurrentPrompt,
  resolveOutput,
  resolvePrompt,
  onStepComplete,
  onCommandComplete,
  engine,
  commands,
  activeCommandName, // Command name saved before engine processing
}) {
  // Use the command name passed from the caller, fallback to engine if not provided
  const completedCommandName = activeCommandName || engine.currentCommandName;

  const setOutput = useCommandFlowStore.getState().setOutput;
  const setOutputWithDelay = useCommandFlowStore.getState().setOutputWithDelay;
  const addOutputLinesWithDelay =
    useCommandFlowStore.getState().addOutputLinesWithDelay;
  const addOutputWithTypingEffect =
    useCommandFlowStore.getState().addOutputWithTypingEffect;
  const setIsLoading = useCommandFlowStore.getState().setIsLoading;
  const setInSession = useCommandFlowStore.getState().setInSession;
  let handled = false;

  // Handle output - use typing effect for system responses
  if (result.output) {
    console.log("📤 handleStepComplete - displaying output:", result.output);
    const resolvedOutput = await resolveOutput(result.output);

    // Push the whole resolved output as a SINGLE entry. CSS white-space:
    // pre-wrap (TerminalOutput) renders embedded \n as line breaks, while
    // keeping it as one entry means the typing animation skips with a
    // single Enter/Space across all lines instead of needing one keypress
    // per line.
    let outputText = resolvedOutput;
    if (typeof outputText === "string") {
      // Trim trailing blank lines to prevent a loading flash
      outputText = outputText.replace(/(?:\n\s*)+$/, "");
    }
    if (typeof outputText !== "string" || outputText.length > 0) {
      await addOutputWithTypingEffect([
        { type: "output", text: parseMarkedText(outputText) },
      ]);
    }
    handled = true;

    // If a prompt is coming next, re-set isLoading so the ">" default prompt
    // doesn't flash between output finishing and the prompt typing starting.
    if (result.prompt) {
      useCommandFlowStore.setState({ isLoading: true });
    }
  }

  // Handle prompt
  if (result.prompt) {
    const resolvedPrompt = await resolvePrompt(result.prompt);

    // Check if prompt contains multiple lines and split them for delayed display
    if (typeof resolvedPrompt === "string" && resolvedPrompt.includes("\n")) {
      const lines = resolvedPrompt.split("\n");
      const lastLine = lines.pop(); // Remove the last line to use as prompt

      if (lines.length > 0) {
        // Join into a single output entry so one Enter skips the whole block.
        // CSS `white-space: pre-wrap` renders the embedded \n as line breaks.
        const outputLines = [
          { type: "output", text: parseMarkedText(lines.join("\n")) },
        ];
        await addOutputWithTypingEffect(outputLines);
        // Re-set isLoading so the ">" doesn't flash before the prompt typing starts
        useCommandFlowStore.setState({ isLoading: true });
      }

      // Set only the last line as the current prompt with typing effect
      const setCurrentPromptWithTypingEffect =
        useCommandFlowStore.getState().setCurrentPromptWithTypingEffect;
      await setCurrentPromptWithTypingEffect(stripMarkers(lastLine));
    } else {
      // Single-line prompt - use typing effect
      const setCurrentPromptWithTypingEffect =
        useCommandFlowStore.getState().setCurrentPromptWithTypingEffect;
      await setCurrentPromptWithTypingEffect(stripMarkers(resolvedPrompt));
    }
    handled = true;
  }

  // Handle session end — but NOT if we just displayed a new prompt
  // (engine session is transiently ended during redirect chains).
  if (!engine.isInSession() && !result.prompt) {
    // Use atomic setState to prevent intermediate renders that flash the loading indicator
    useCommandFlowStore.setState({
      currentPrompt: null,
      inSession: false,
      isLoading: false,
    });

    // Store the session data before ending
    const sessionData = engine.session?.data || {};
    if (Object.keys(sessionData).length > 0) {
      storePayload(sessionData);
    }

    if (onCommandComplete) {
      onCommandComplete(completedCommandName, sessionData);
    }

    // Clear all command state since command is complete
    clearAllCommandState();

    return true; // Return true since we handled the session end
  }

  if (handled && onStepComplete) {
    onStepComplete(engine.currentCommandName);
  }

  return handled;
}
